import React, { useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Plus, Building2, Layers, Upload, FileSpreadsheet, Archive, Pencil, Loader2, Info } from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import { StatusBadge, StatCard, Toast, getCurrentRole } from "../components/diagnostic/primitives";
import TestsDrawer from "../components/diagnostic/TestsDrawer";
import { getChainTestListTests, updateChainTestList, updateChainTestListTest } from "../services/diagnosticLabsService";
import { useChainTestLists, useUploadPolling } from "../components/diagnostic/chainTests/useChainTestLists";
import {
  Panel, EmptyState, ErrorCard, Skeleton, Dialog, PrimaryButton, SecondaryButton, plural,
} from "../components/diagnostic/chainTests/ui";
import TestListCard from "../components/diagnostic/chainTests/TestListCard";
import MemberLabsTable from "../components/diagnostic/chainTests/MemberLabsTable";
import TestListUploadDrawer from "../components/diagnostic/chainTests/TestListUploadDrawer";
import AssignLabsDrawer from "../components/diagnostic/chainTests/AssignLabsDrawer";

// Chain Test Lists workspace — manage the chain's Excel test lists and which
// member labs use each. Everything is driven by the chain/list/lab ids returned
// by the API; nothing here knows about specific chains or labs.
const DiagnosticChainTestLists = () => {
  const navigate = useNavigate();
  const { chainId } = useParams();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const role = getCurrentRole();
  const canAccess = role === "Ops" || role === "SuperAdmin";

  const { chain, lists, labs, loading, error, refresh, replaceList } = useChainTestLists(role, chainId);
  const [toast, setToast] = useState(null);
  const [uploadTarget, setUploadTarget] = useState(null); // { mode: "new" } | { mode: "version", list }
  const [assignList, setAssignList] = useState(null);
  const [viewList, setViewList] = useState(null);
  const [renameState, setRenameState] = useState(null); // { list, name, busy, error }
  const [archiveState, setArchiveState] = useState(null); // { list, busy, error }

  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3400);
  }, []);

  // A settled upload changes lab counts, so refresh the member list quietly.
  const onSettled = useCallback(() => refresh({ silent: true }), [refresh]);
  useUploadPolling(role, chainId, lists, replaceList, onSettled);

  const labNameById = useMemo(() => new Map(labs.map((l) => [l.id, l.brand_name])), [labs]);
  const managedCount = labs.filter((l) => l.test_source === "chain").length; // contract: loan-and-subscription/internalapi/views/user_type/shared/diagnostic/chain/chain_test_list_serializers.py serialize_chain_member_labs test_source
  const readOnly = chain ? !chain.active : true;

  const loadListTests = useCallback(
    (params) => getChainTestListTests(role, chainId, viewList?.id, params),
    [role, chainId, viewList?.id]
  );

  const submitRename = async () => {
    const name = renameState.name.trim();
    if (!name) return setRenameState((s) => ({ ...s, error: "Name is required." }));
    setRenameState((s) => ({ ...s, busy: true, error: "" }));
    try {
      replaceList(await updateChainTestList(role, chainId, renameState.list.id, { name }));
      setRenameState(null);
      showToast("success", "List renamed.");
    } catch (err) {
      setRenameState((s) => ({ ...s, busy: false, error: err.message }));
    }
  };

  const submitArchive = async () => {
    setArchiveState((s) => ({ ...s, busy: true, error: "" }));
    try {
      await updateChainTestList(role, chainId, archiveState.list.id, { active: false });
      setArchiveState(null);
      showToast("success", "List archived.");
      refresh({ silent: true });
    } catch (err) {
      setArchiveState((s) => ({ ...s, busy: false, error: err.message }));
    }
  };

  if (!authLoading && (!isAuthenticated || !canAccess)) {
    return (
      <div className="flex justify-center items-center h-64 text-center">
        <div>
          <h2 className="text-2xl font-bold text-red-600 mb-2">Access Denied</h2>
          <p className="text-gray-600">You do not have permission to access this page.</p>
        </div>
      </div>
    );
  }

  const stats = [
    { icon: Building2, label: "Member labs", value: labs.length, accent: { bg: "bg-[#EAF2FA]", fg: "text-[#0F4C81]", bar: "bg-[#0F4C81]" } },
    { icon: Layers, label: "On chain lists", value: managedCount, accent: { bg: "bg-[#E6F6F6]", fg: "text-[#00A6A6]", bar: "bg-[#00A6A6]" } },
    { icon: Upload, label: "Direct upload", value: labs.length - managedCount, accent: { bg: "bg-slate-100", fg: "text-slate-600", bar: "bg-slate-400" } },
    { icon: FileSpreadsheet, label: "Test lists", value: lists.length, accent: { bg: "bg-[#E9F8F1]", fg: "text-[#2EBD85]", bar: "bg-[#2EBD85]" } },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F1F8F8] to-[#E5F1F1] p-4 sm:p-8 flex justify-center">
      <div className="w-full max-w-7xl space-y-6">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button type="button" onClick={() => navigate(`/subscriber-wallet/labs/chains/${chainId}`)} aria-label="Back to chain"
              className="p-2 text-gray-500 hover:text-gray-900 hover:bg-white rounded-lg border border-transparent hover:border-gray-200 transition-all shrink-0">
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div className="min-w-0">
              <p className="text-xs font-medium text-[#00A6A6]">{chain?.brand_name || (loading ? "Loading…" : "Chain")}</p>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold text-gray-900">Test lists</h1>
                {chain && <StatusBadge status={chain.active ? "active" : "inactive"} />}
              </div>
            </div>
          </div>
          {!readOnly && (
            <PrimaryButton onClick={() => setUploadTarget({ mode: "new" })}>
              <Plus className="w-4 h-4" /> New test list
            </PrimaryButton>
          )}
        </div>

        {chain && !chain.active && (
          <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            <Info className="w-4 h-4 mt-0.5 shrink-0" />
            This chain is inactive, so its test lists are not in effect — every member lab uses its own direct test upload. Activate the chain to manage lists.
          </div>
        )}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((s, i) => <StatCard key={s.label} {...s} loading={loading} index={i} />)}
        </div>

        {error ? (
          <Panel title="Test lists"><ErrorCard message={error} onRetry={() => refresh()} /></Panel>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
            {/* Test list sources */}
            <div className="lg:col-span-5 space-y-3">
              <div className="flex items-baseline justify-between px-1">
                <h2 className="text-sm font-semibold text-[#0F4C81]">Test list sources</h2>
                <span className="text-[11px] text-gray-400">One Excel = one list · a lab uses at most one</span>
              </div>
              {loading ? (
                [0, 1].map((i) => <Skeleton key={i} className="h-44 rounded-2xl" />)
              ) : lists.length === 0 ? (
                <Panel title="No test lists yet">
                  <EmptyState
                    icon={FileSpreadsheet}
                    title="Upload the chain's first test list"
                    description="Upload one Excel and apply it to any member labs. Labs you don't assign keep using their own direct upload."
                    action={!readOnly && <PrimaryButton onClick={() => setUploadTarget({ mode: "new" })}><Plus className="w-4 h-4" /> New test list</PrimaryButton>}
                  />
                </Panel>
              ) : (
                lists.map((list, i) => (
                  <TestListCard
                    key={list.id}
                    index={i}
                    list={list}
                    readOnly={readOnly}
                    labNames={list.assigned_lab_ids.map((id) => labNameById.get(id)).filter(Boolean)} // ok: drops ids of labs not in the member list
                    onViewTests={setViewList}
                    onUpload={(l) => setUploadTarget({ mode: "version", list: l })}
                    onManageLabs={setAssignList}
                    onRename={(l) => setRenameState({ list: l, name: l.name, busy: false, error: "" })}
                    onArchive={(l) => setArchiveState({ list: l, busy: false, error: "" })}
                  />
                ))
              )}
            </div>

            {/* Member labs */}
            <motion.div layout className="lg:col-span-7">
              <Panel title="Member labs" subtitle="Where each lab's live test menu comes from">
                <MemberLabsTable labs={labs} lists={lists} loading={loading} />
              </Panel>
            </motion.div>
          </div>
        )}
      </div>

      <TestListUploadDrawer
        open={!!uploadTarget}
        onClose={() => setUploadTarget(null)}
        role={role}
        chainId={chainId}
        target={uploadTarget}
        lists={lists}
        labs={labs}
        onUploaded={replaceList}
        onAssigned={() => refresh({ silent: true })}
        onToast={showToast}
      />
      <AssignLabsDrawer
        open={!!assignList}
        onClose={() => setAssignList(null)}
        role={role}
        chainId={chainId}
        list={assignList ? lists.find((l) => l.id === assignList.id) || assignList : null}
        labs={labs}
        onSaved={(message) => {
          if (message) showToast("success", message);
          refresh({ silent: true });
        }}
      />
      <TestsDrawer
        open={!!viewList}
        onClose={() => setViewList(null)}
        role={role}
        labName={viewList?.name || ""}
        subtitle={viewList ? `${viewList.name} · chain test list` : ""}
        loadTests={loadListTests}
        onSaveTest={readOnly ? undefined : (test, payload) => updateChainTestListTest(role, chainId, viewList.id, test.id, payload)}
        editNote={viewList
          ? `Edits go live immediately for all ${plural(viewList.assigned_lab_ids.length, "lab")} using “${viewList.name}”. The next upload of this list replaces them.`
          : undefined}
        readOnlyNote={readOnly ? "This chain is inactive — its test lists are read-only." : undefined}
      />

      <Dialog
        open={!!renameState}
        onClose={() => setRenameState(null)}
        busy={renameState?.busy}
        icon={Pencil}
        tone="teal"
        title="Rename test list"
        actions={(
          <>
            <SecondaryButton onClick={() => setRenameState(null)} disabled={renameState?.busy}>Cancel</SecondaryButton>
            <PrimaryButton onClick={submitRename} disabled={renameState?.busy}>
              {renameState?.busy && <Loader2 className="w-4 h-4 animate-spin" />} Save
            </PrimaryButton>
          </>
        )}
      >
        <input
          autoFocus
          value={renameState?.name || ""}
          onChange={(e) => setRenameState((s) => ({ ...s, name: e.target.value.slice(0, 255), error: "" }))}
          onKeyDown={(e) => e.key === "Enter" && submitRename()}
          className="mt-1 w-full px-3 py-2 rounded-lg border border-[#CFE0E0] text-sm focus:ring-2 focus:ring-[#00A6A6]/50 focus:border-[#00A6A6]"
        />
        {renameState?.error && <p className="mt-1.5 text-xs text-red-600">{renameState.error}</p>}
      </Dialog>

      <Dialog
        open={!!archiveState}
        onClose={() => setArchiveState(null)}
        busy={archiveState?.busy}
        icon={Archive}
        tone="red"
        title={`Archive “${archiveState?.list?.name || ""}”?`}
        actions={(
          <>
            <SecondaryButton onClick={() => setArchiveState(null)} disabled={archiveState?.busy}>Cancel</SecondaryButton>
            <button type="button" onClick={submitArchive} disabled={archiveState?.busy || archiveState?.list?.assigned_lab_ids.length > 0}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              {archiveState?.busy && <Loader2 className="w-4 h-4 animate-spin" />} Archive
            </button>
          </>
        )}
      >
        {archiveState?.list?.assigned_lab_ids.length > 0 ? (
          <p>
            {plural(archiveState.list.assigned_lab_ids.length, "lab")} still use this list. Remove them from it with
            “Manage labs” first — each will then revert to its own direct test list.
          </p>
        ) : (
          <p>The list and its upload history are kept for audit but it can no longer be assigned. This can't be undone.</p>
        )}
        {archiveState?.error && <p className="mt-2 text-xs text-red-600">{archiveState.error}</p>}
      </Dialog>

      <Toast toast={toast} />
    </div>
  );
};

export default DiagnosticChainTestLists;
